/**
 * ProduktVorschau — was veröffentlicht würde (Inhalt von Konzept A).
 *
 * @description
 *   Zeigt Titel, Preis/Streichpreis, den Zustandsabsatz und die geerbte
 *   A-Beschreibung — je Feld aus EINER Quelle. Der Zustandsabsatz und der
 *   komponierte Text kommen aus `GET /api/second-life/vorschau` (lesend); Preis
 *   und Streichpreis aus der Zeile (`price_eur`/`vergleichspreis_eur`).
 *   Drei Zustände sind drei Flächen (Regel 22 §7): **lädt** (Skelettzeile),
 *   **Fehler** (Warnfarbe + „Try again") und **Antwort** (Absatz oder der Satz,
 *   der sagt, warum es noch keinen gibt).
 *
 * @useWhen
 *   - Das Detail eines Falls zeigt, was in den Shop ginge
 *
 * @dontUseFor
 *   - Eine Eingabe — der Editor steht in der Bestandstabelle
 *
 * @related
 *   - `FallDetail.jsx` — der Rahmen
 *   - `second_life.py::vorschau` — die lesende Route
 *
 * @props
 *   zeile     Die Fallzeile (Titel-Ersatz, Preis, Streichpreis)
 *   vorschau  Die Antwort der Vorschau-Route oder null (lädt/fehlt)
 *   fehler    Ein Satz, wenn die Vorschau nicht lesbar war (502)
 *   laedt     true, solange die Antwort aussteht (R16-C3)
 *   onNeu     () => … — die Vorschau erneut lesen (R16-C6)
 */
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { SP, TOUCH_TARGET_COARSE } from '../../theme/tokens';
import { v2Tone } from '../../theme/theme';
import { euroText } from './bestand';

/** Die Lesespalte der Vorschau — 72 Zeichen sind das obere Maß (R16-C8). */
const TEXT_MAX = '72ch';

export default function ProduktVorschau({ zeile, vorschau, fehler, laedt = false, onNeu }) {
  const titel = vorschau?.titel || zeile?.name || 'Product';
  const preis = zeile?.price_eur;
  const streich = vorschau?.a_vk ?? zeile?.vergleichspreis_eur;
  const absatz = vorschau?.zustand_html || null;
  const aHtml = vorschau?.a_beschreibung_html || null;
  return (
    <Box data-testid="produkt-vorschau">
      <Typography variant="subtitle1" fontWeight={600}>{titel}</Typography>
      <Stack direction="row" spacing={SP.sm} alignItems="baseline" sx={{ mb: SP.chip }}>
        <Typography variant="body2">{euroText(preis)}</Typography>
        {streich != null && preis != null && Number(streich) !== Number(preis) && (
          <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
            {euroText(streich)}
          </Typography>
        )}
      </Stack>
      {laedt ? (
        // 🔴 **Laden ist ein eigener Zustand** (R16-C3): hier stand der Satz
        // „written when the draft is created" — eine FALSCHE Aussage für einen
        // Fall, der Entwurf und Text längst hat.
        <Box data-testid="vorschau-laedt" aria-busy="true">
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="45%" />
        </Box>
      ) : fehler ? (
        <Box data-testid="vorschau-fehler">
          <Typography
            variant="caption"
            sx={{ display: 'block', color: (t) => v2Tone(t, 'warn').main, mb: SP.chip }}
          >
            {fehler}
          </Typography>
          {onNeu && (
            <Button size="small" variant="outlined" sx={{ ...TOUCH_TARGET_COARSE }} onClick={onNeu}>
              Try again
            </Button>
          )}
        </Box>
      ) : (
        <>
          {absatz ? (
            // Zustandsabsatz ist KLARTEXT (escaped vom Server) — als Text gezeigt.
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: SP.chip, maxWidth: TEXT_MAX }}>
              {absatz}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: SP.chip, maxWidth: TEXT_MAX }}>
              The description for the product page is written when the draft is created.
              {' '}The item description from the base product is already shown below.
            </Typography>
          )}
          {aHtml ? (
            // 🔴 Die A-Beschreibung IST HTML (descriptionHtml) — unverändert
            // durchgereicht, nicht escaped (sonst stünden die Tags als Text).
            <Box
              data-testid="a-beschreibung"
              sx={{ typography: 'body2', maxWidth: TEXT_MAX, '& p': { m: 0, mb: SP.chip } }}
              // eslint-disable-next-line react/no-danger -- Server-HTML des
              // Grundprodukts, dieselbe Quelle wie der Schreibweg (`_beschreibung_html`)
              dangerouslySetInnerHTML={{ __html: aHtml }}
            />
          ) : null}
        </>
      )}
    </Box>
  );
}
